import { IconButton, OdinIcon, OdinIconSize, type DialogRef } from '@myosh/odin-components';
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../redux/hooks';
import { deleteGroupOrField } from '../../redux/slices/form-builder-slice';
import ConfirmationDialog from '../common/confirmation-dialog.component';

interface FormBuilderAccordionProps {
  id: number | string;
  caption: string;
  onGroupClick: () => void;
  children: React.ReactNode;
  className?: string;
}

const FormBuilderAccordion = ({ id, onGroupClick, caption, children, className }: FormBuilderAccordionProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [open, setOpen] = useState(true);
  const deleteModalRef = useRef<DialogRef>(null);

  const onDeleteHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    deleteModalRef.current?.show();
  };

  const handleConfirmDelete = () => {
    if (id) {
      dispatch(deleteGroupOrField({ type: 'GROUP', id: id }));
    }
  };

  const handleCloseModal = () => {
    deleteModalRef.current?.close();
  };

  const onArrowClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(!open);
  };

  const onSummaryClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onGroupClick?.();
  };

  const styles = clsx(
    'mb-4 flex-1 cursor-pointer rounded-md border border-gray-4 bg-mono-1 p-2 outline-2 outline-primary-2 [&:has(.arrow-button:hover)]:opacity-100 [&:has(.arrow-button:hover)]:outline-none [&:has(.header-summary:hover)]:opacity-50 [&:has(.header-summary:hover)]:outline',
    className
  );

  return (
    <>
      <div className={styles} key={id}>
        <div className="mb-4 flex list-none items-center justify-between gap-2 border-b border-solid border-gray-2 text-lg font-semibold">
          <div className="header-summary flex flex-1 items-center" onClick={onSummaryClick}>
            <span className="overflow-wrap flex flex-wrap break-words">{caption}</span>
          </div>
          {id !== 'HIERARCHIES' && (
            <IconButton
              icon="Trash"
              iconSize={OdinIconSize.Medium}
              iconClassName="w-5"
              classNames="details-action"
              onClick={onDeleteHandler}
            />
          )}
          <div onClick={onArrowClick} className="arrow-button p-1 transition-transform">
            <OdinIcon icon={open ? 'ChevronUp' : 'ChevronDown'} />
          </div>
        </div>

        {open && children}
      </div>
      <ConfirmationDialog
        ref={deleteModalRef}
        header={t('this-action-cannot-be-undone')}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseModal}
        confirmButtonText={t('yes')}
        cancelButtonText={t('no')}
        hasCancelButton
      >
        <p>{t('field-group-delete-message')}</p>
      </ConfirmationDialog>
    </>
  );
};

export default FormBuilderAccordion;
